// WaveSurfer is now loaded on demand (dynamic import) instead of at page load.
// The audio preview feature is unchanged: waveforms still render and play,
// but the library and audio files no longer compete with the initial page load.

let wavesurferPromise = null;
function loadWaveSurfer() {
  if (!wavesurferPromise) {
    wavesurferPromise = import('./wavesurfer.esm.js').then((m) => m.default);
  }
  return wavesurferPromise;
}

// Resolves after the page has fully loaded and the browser is idle,
// so eager waveforms never compete with images/CSS/JS of the initial load.
function afterPageSettled() {
  return new Promise((resolve) => {
    const idle = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(resolve, { timeout: 3000 });
      } else {
        setTimeout(resolve, 1500);
      }
    };
    if (document.readyState === 'complete') {
      idle();
    } else {
      window.addEventListener('load', idle, { once: true });
    }
  });
}

class ProductCardAudio extends HTMLElement {
  static current = null;

  connectedCallback() {
    this.waveformEl = this.querySelector('.product-card-audio__waveform');
    this.btn = this.querySelector('.product-card-audio__btn');
    this.iconPlay = this.querySelector('.product-card-audio__icon--play');
    this.iconPause = this.querySelector('.product-card-audio__icon--pause');
    this.src = this.dataset.src;
    this.eager = this.dataset.eager === 'true';
    this.ws = null;
    this.initPromise = null;

    this.btn.addEventListener('click', this.onButtonClick.bind(this));

    if (this.eager) {
      // Eager players (hero/PDP): build the waveform when it scrolls near the
      // viewport, but only after the page has finished loading.
      afterPageSettled().then(() => {
        if (!this.isConnected || this.ws || this.initPromise) return;
        this.observer = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) {
            this.observer.disconnect();
            this.initWaveSurfer();
          }
        }, { rootMargin: '300px' });
        this.observer.observe(this);
      });
    }
    // Non-eager (collection/homepage cards): waveform is built on first click only.
  }

  initWaveSurfer() {
    if (this.ws || this.initPromise || !this.waveformEl) return this.initPromise;

    this.initPromise = loadWaveSurfer().then((WaveSurfer) => {
      if (this.ws || !this.isConnected) return;

      const base = getComputedStyle(this.waveformEl).color;        // "rgb(r, g, b)"
      const rgb = (base.match(/\d+/g) || ['21', '21', '21']).map(Number);

      // Opaque light tint of the body color — NOT a low-alpha color.
      // (wavesurfer's source-in progress mask inherits waveColor's alpha,
      //  so a transparent waveColor makes the progress transparent too.)
      const mix = (c) => Math.round(c * 0.25 + 255 * 0.75);
      const waveColor = `rgb(${mix(rgb[0])}, ${mix(rgb[1])}, ${mix(rgb[2])})`;

      const accent = getComputedStyle(this).getPropertyValue('--color-accent').trim();

      this.ws = WaveSurfer.create({
        container: this.waveformEl,
        url: this.src,
        height: 36,
        waveColor: waveColor,                 // opaque light grey
        progressColor: accent || base,        // opaque accent / body color
        cursorWidth: 0,
        barWidth: 2,
        barGap: 2,
        barRadius: 2,
        normalize: true,
      });

      this.ws.on('finish', () => { this.stop(); this.ws.seekTo(0); });
      this.ws.on('interaction', () => {
        if (!this.ws.isPlaying()) this.play();
      });
      return this.ws;
    }).catch(() => {
      // Dynamic import failed (offline/old browser): fall back to a plain
      // <audio> element so the preview still plays.
      this.initPromise = null;
      if (!this.fallbackAudio && this.src) {
        this.fallbackAudio = new Audio(this.src);
        this.fallbackAudio.addEventListener('ended', () => this.stop());
      }
    });
    return this.initPromise;
  }

  onButtonClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (this.ws) {
      if (this.ws.isPlaying()) this.stop(); else this.play();
      return;
    }
    if (this.fallbackAudio) {
      if (!this.fallbackAudio.paused) this.stop(); else this.play();
      return;
    }
    const p = this.initWaveSurfer();
    if (p) p.then(() => { if (this.ws || this.fallbackAudio) this.play(); });
  }

  play() {
    if (ProductCardAudio.current && ProductCardAudio.current !== this) {
      ProductCardAudio.current.stop();
    }
    ProductCardAudio.current = this;
    if (this.ws) this.ws.play();
    else if (this.fallbackAudio) this.fallbackAudio.play();
    this.classList.add('is-playing');
    this.btn.setAttribute('aria-pressed', 'true');
    this.iconPlay.hidden = true;
    this.iconPause.hidden = false;
  }

  stop() {
    if (this.ws) this.ws.pause();
    if (this.fallbackAudio) this.fallbackAudio.pause();
    this.classList.remove('is-playing');
    this.btn.setAttribute('aria-pressed', 'false');
    this.iconPlay.hidden = false;
    this.iconPause.hidden = true;
    if (ProductCardAudio.current === this) ProductCardAudio.current = null;
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.ws) { this.ws.destroy(); this.ws = null; }
    if (this.fallbackAudio) { this.fallbackAudio.pause(); this.fallbackAudio = null; }
  }
}

if (!customElements.get('product-card-audio')) {
  customElements.define('product-card-audio', ProductCardAudio);
}

// Stop playback when a product is added to the cart.
document.addEventListener('cart:item-added', () => {
  if (ProductCardAudio.current) ProductCardAudio.current.stop();
});
